import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSyncQueue, localGallery, localInquiries, localMessages, localNews, localPartners, localProducts, localTeamMembers } from '@siaedts/shared';
import { db } from '@siaedts/shared/lib/supabase';
import { useLanguage } from '../i18n';

export default function AdminDashboard() {
  const { language, t } = useLanguage();
  const [counts, setCounts] = useState({
    products: 0,
    news: 0,
    totalInquiries: 0,
    newInquiries: 0,
    totalMessages: 0,
    unreadMessages: 0,
    gallery: 0,
    partners: 0,
    team: 0,
    staffUsers: 0,
    pending: 0,
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    void refresh();
  }, []);

  async function refresh() {
    setLoading(true);
    const [products, news, inquiries, messages, gallery, partners, team, queue] = await Promise.all([
      localProducts.list(),
      localNews.list(),
      localInquiries.list(),
      localMessages.list(),
      localGallery.list(),
      localPartners.list(),
      localTeamMembers.list(),
      getSyncQueue(),
    ]);

    let remoteProducts = products;
    let remoteNews = news;
    let remoteInquiries = inquiries;
    let remoteMessages = messages;
    let remoteGallery = gallery;
    let remotePartners = partners;
    let remoteTeam = team;
    let staffUsersCount = 1;

    if (navigator.onLine) {
      try {
        const [prod, articles, inq, msg, galleryItems, partnerItems, teamItems, usr] = await Promise.all([
          db.products.getAll().catch(() => null),
          db.news.getAll().catch(() => null),
          db.inquiries.getAll().catch(() => null),
          db.contactMessages.getAll().catch(() => null),
          db.gallery.getAll().catch(() => null),
          db.partners.getAll({ is_active: true }).catch(() => null),
          db.team.getAll({ is_active: true }).catch(() => null),
          db.users.getAll().catch(() => null),
        ]);
        if (prod && Array.isArray(prod)) remoteProducts = prod as typeof products;
        if (articles && Array.isArray(articles)) remoteNews = articles as typeof news;
        if (inq && Array.isArray(inq)) remoteInquiries = inq as typeof inquiries;
        if (msg && Array.isArray(msg)) remoteMessages = msg as typeof messages;
        if (galleryItems && Array.isArray(galleryItems)) remoteGallery = galleryItems as typeof gallery;
        if (partnerItems && Array.isArray(partnerItems)) remotePartners = partnerItems as typeof partners;
        if (teamItems && Array.isArray(teamItems)) remoteTeam = teamItems as typeof team;
        if (usr && Array.isArray(usr)) staffUsersCount = usr.length;
      } catch (err) {
        console.warn('Could not fetch remote dashboard counts:', err);
      }
    }

    setCounts({
      products: remoteProducts.length,
      news: remoteNews.length,
      totalInquiries: remoteInquiries.length,
      newInquiries: remoteInquiries.filter((item) => item.status === 'new').length,
      totalMessages: remoteMessages.length,
      unreadMessages: remoteMessages.filter((item) => item.status === 'unread').length,
      gallery: remoteGallery.length,
      partners: remotePartners.length,
      team: remoteTeam.length,
      staffUsers: staffUsersCount,
      pending: queue.length,
    });
    setLoading(false);
  }

  const cards = [
    [t('totalProducts'), counts.products, 'products', 'blue', t('catalogRecords')],
    [t('totalNews'), counts.news, 'news', 'green', t('publishedDraft')],
    [t('newInquiries'), counts.newInquiries, 'inquiries', 'yellow', t('needsAttention')],
    [t('unreadMessages'), counts.unreadMessages, 'messages', 'red', t('contactMessages')],
  ] as const;
  const overview = [
    [t('totalInquiries'), counts.totalInquiries, 'inquiries'], [t('pendingSync'), counts.pending, 'dashboard'],
    [t('staffUsers'), counts.staffUsers, 'users'], [t('galleryItems'), counts.gallery, 'gallery'],
    [t('activePartners'), counts.partners, 'partners'], [t('teamMembers'), counts.team, 'team'],
  ] as const;

  return <div className="legacy-page">
    <div className="section-titlebar dashboard-titlebar"><div><p className="eyebrow">{t('operations')}</p><h2>{t('adminDashboard')}</h2><p className="section-titlebar-copy">{language === 'fr' ? 'Suivez votre activité et gardez votre espace de travail sous contrôle.' : 'Track your activity and keep your workspace under control.'}</p></div><button className="secondary-button titlebar-refresh-button" type="button" onClick={() => void refresh()} disabled={loading}>{loading ? t('refreshing') : t('refresh')}</button></div>
    <div className="summary-grid legacy-summary">{cards.map(([label, value, path, color, detail]) => <Link className="summary-card dashboard-card" key={label} to={`/admin/${path}`}><div className="dashboard-card-top"><div><span>{label}</span><strong>{value.toLocaleString()}</strong></div><span className={`dashboard-icon ${color}`} aria-hidden="true"><DashboardIcon name={path} /></span></div><small>{detail}</small>{value > 0 && (path === 'inquiries' || path === 'messages') && <em>{value} new</em>}</Link>)}</div>
    <section className="legacy-panel overview-panel"><div className="panel-heading"><div><p className="eyebrow">{t('localWorkspace')}</p><h2>{t('overview')}</h2></div><Link className="secondary-button" to="/admin/products">{t('manageProducts')}</Link></div><div className="overview-list">{overview.map(([label, value, path]) => <Link key={label} to={`/admin/${path}`}><span>{label}</span><strong>{value.toLocaleString()}</strong></Link>)}</div></section>
  </div>;
}

function DashboardIcon({ name }: { name: string }) {
  const paths: Record<string, string> = {
    products: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
    news: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2M9 5h6M7 16h6M7 8h6v4H7V8z',
    inquiries: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 3.582-8 9-8s9 3.582 9 8z',
    messages: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  };
  return <svg className="dashboard-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={paths[name] ?? paths.products} /></svg>;
}
